import * as arrow from "apache-arrow";

export interface ChartDataResult {
  dataset: Array<Record<string, any>>;
  xAxisData: any[];
}

export function getChartDataFromArrowTable({
  arrowTable,
  columns,
  xAxisField,
  yAxisFields,
  datasetLabels = {},
  datasetColors = {},
  measureFilter,
  segments = [],
}: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  xAxisField: string;
  yAxisFields: string[];
  datasetLabels?: Record<string, string>;
  datasetColors?: Record<string, string>;
  measureFilter?: string;
  segments?: string[];
}): ChartDataResult | null {
  const numRows = arrowTable.numRows;

  // Get vectors for x-axis and y-axis fields
  const xAxisVector = arrowTable.getChild(xAxisField);
  const yAxisVectors = yAxisFields.map((field) => arrowTable.getChild(field));
  const measureVector = measureFilter ? arrowTable.getChild("measure") : null;

  // Get segment vectors if segments are specified
  const segmentVectors = segments.map((segment) =>
    arrowTable.getChild(segment)
  );

  if (
    !xAxisVector ||
    !yAxisVectors.every((v) => v) ||
    (measureFilter && !measureVector) ||
    (segments.length > 0 && !segmentVectors.every((v) => v))
  ) {
    return null;
  }

  // Get unique x-axis values and sort them
  const xAxisValueSet = new Set<any>();
  for (let i = 0; i < numRows; i++) {
    // Only filter by measure if measureFilter is provided and measureVector exists
    if (measureFilter && measureVector) {
      const measureValue = measureVector.get(i);
      // Only include rows that match the selected measure (case-insensitive)
      if (String(measureValue).toLowerCase() !== measureFilter.toLowerCase()) {
        continue;
      }
    }

    const value = xAxisVector.get(i);
    if (value !== null && value !== undefined) {
      xAxisValueSet.add(value);
    }
  }

  if (xAxisValueSet.size === 0) {
    return null;
  }

  const xAxisData = Array.from(xAxisValueSet).sort((a, b) => {
    // Handle different data types for sorting
    if (typeof a === "number" && typeof b === "number") {
      return a - b;
    }
    if (a instanceof Date && b instanceof Date) {
      return a.getTime() - b.getTime();
    }
    if (typeof a === "string" && typeof b === "string") {
      return a.localeCompare(b);
    }
    return 0;
  });

  // If segments are specified, create multiple series
  if (segments.length > 0) {
    // Get unique segment combinations
    const segmentCombinations = new Set<string>();
    for (let i = 0; i < numRows; i++) {
      if (measureFilter && measureVector) {
        const measureValue = measureVector.get(i);
        if (
          String(measureValue).toLowerCase() !== measureFilter.toLowerCase()
        ) {
          continue;
        }
      }

      const segmentValues = segmentVectors.map(
        (vector) => vector?.get(i) || "Unknown"
      );
      const combination = segmentValues.join("|");
      segmentCombinations.add(combination);
    }

    const dataset: Array<Record<string, any>> = [];

    // For each x-axis value (date), create a row with data for each segment combination
    for (const xValue of xAxisData) {
      const row: Record<string, any> = {
        x: xValue, // x-axis value (date)
      };

      // Add data for each segment combination
      for (const combination of segmentCombinations) {
        const segmentValues = combination.split("|");
        const seriesName = segmentValues.join(" - ");

        // Add each y-axis field value for this x-axis value and segment combination
        for (let i = 0; i < yAxisFields.length; i++) {
          const field = yAxisFields[i];
          const vector = yAxisVectors[i]!;
          let sum = 0;

          // Sum all values for this x-axis value and segment combination
          for (let j = 0; j < numRows; j++) {
            // Only filter by measure if measureFilter is provided and measureVector exists
            if (measureFilter && measureVector) {
              const measureValue = measureVector.get(j);
              if (
                String(measureValue).toLowerCase() !==
                measureFilter.toLowerCase()
              ) {
                continue;
              }
            }

            const xAxisValue = xAxisVector.get(j);
            if (
              xAxisValue !== null &&
              xAxisValue !== undefined &&
              xAxisValue === xValue
            ) {
              // Check if this row matches the segment combination
              const currentSegmentValues = segmentVectors.map(
                (vector) => vector?.get(j) || "Unknown"
              );
              const currentCombination = currentSegmentValues.join("|");

              if (currentCombination === combination) {
                const yValue = vector.get(j);
                if (yValue !== null && yValue !== undefined) {
                  const value = Number(yValue) || 0;
                  sum += value;
                }
              }
            }
          }

          // Use the series name as the field name
          row[seriesName] = sum;
        }
      }

      dataset.push(row);
    }

    return {
      dataset,
      xAxisData,
    };
  }

  // No segments - original logic
  const dataset: Array<Record<string, any>> = [];

  // For each x-axis value (date), create a row with aggregated y-axis values
  for (const xValue of xAxisData) {
    const row: Record<string, any> = {
      x: xValue, // x-axis value (date)
    };

    // Add each y-axis field value for this x-axis value
    for (let i = 0; i < yAxisFields.length; i++) {
      const field = yAxisFields[i];
      const vector = yAxisVectors[i]!;
      let sum = 0;

      // Sum all values for this x-axis value
      for (let j = 0; j < numRows; j++) {
        // Only filter by measure if measureFilter is provided and measureVector exists
        if (measureFilter && measureVector) {
          const measureValue = measureVector.get(j);
          // Only process rows that match the selected measure (case-insensitive)
          if (
            String(measureValue).toLowerCase() !== measureFilter.toLowerCase()
          ) {
            continue;
          }
        }

        const xAxisValue = xAxisVector.get(j);
        if (
          xAxisValue !== null &&
          xAxisValue !== undefined &&
          xAxisValue === xValue
        ) {
          const yValue = vector.get(j);
          if (yValue !== null && yValue !== undefined) {
            const value = Number(yValue) || 0;
            sum += value;
          }
        }
      }

      // Use sum for the bar chart
      row[field] = sum;
    }

    dataset.push(row);
  }

  return {
    dataset,
    xAxisData,
  };
}
